import { useMutation, UseMutationOptions, useQuery, UseQueryOptions } from '@tanstack/react-query'

import {
  createCheckoutSession,
  createFullPortalSession,
  createPlanUpdatePortalSession,
  createPortalSession,
  createResumeSubscriptionPortalSession,
  createSubscriptionCancellationPortal,
  getCheckoutSessionStatus,
  getStripeProducts,
} from '../billing'
import { queryKeys, queryOptions } from '../react-query-config'
import type {
  CheckoutSessionStatusResponseDto,
  CreateCheckoutSessionDto,
  CreateCheckoutSessionResponseDto,
  FullPortalResponseDto,
  PlanUpdatePortalResponseDto,
  PlanUpdateRequestDto,
  PortalSessionResponseDto,
  ResumeSubscriptionPortalResponseDto,
  StripeProductsResponseDto,
  SubscriptionCancellationPortalResponseDto,
} from '../types'

export const useCreateCheckoutSession = (
  options?: UseMutationOptions<CreateCheckoutSessionResponseDto, Error, CreateCheckoutSessionDto>,
) => {
  return useMutation({
    mutationFn: createCheckoutSession,
    ...options,
  })
}

export const useGetCheckoutSessionStatus = (
  sessionId: string,
  options?: UseQueryOptions<CheckoutSessionStatusResponseDto, Error>,
) => {
  const { enabled, ...restOptions } = options || {}

  return useQuery<CheckoutSessionStatusResponseDto, Error>({
    queryKey: queryKeys.billing.checkoutSessionStatus(sessionId),
    queryFn: () => getCheckoutSessionStatus(sessionId),
    enabled: !!sessionId && enabled !== false, // Combine sessionId check with options
    ...queryOptions.billing,
    ...restOptions,
  })
}

export const useGetStripeProducts = (
  options?: UseQueryOptions<StripeProductsResponseDto, Error>,
) => {
  return useQuery<StripeProductsResponseDto, Error>({
    queryKey: queryKeys.billing.stripeProducts(),
    queryFn: getStripeProducts,
    ...queryOptions.billing,
    ...options,
  })
}

export const useCreatePortalSession = (
  options?: UseMutationOptions<PortalSessionResponseDto, Error, void>,
) => {
  return useMutation({
    mutationFn: createPortalSession,
    ...options,
  })
}

export const useCreateSubscriptionCancellationPortal = (
  options?: UseMutationOptions<SubscriptionCancellationPortalResponseDto, Error, void>,
) => {
  return useMutation({
    mutationFn: createSubscriptionCancellationPortal,
    ...options,
  })
}

export const useCreateResumeSubscriptionPortalSession = (
  options?: UseMutationOptions<ResumeSubscriptionPortalResponseDto, Error, void>,
) => {
  return useMutation({
    mutationFn: createResumeSubscriptionPortalSession,
    ...options,
  })
}

export const useCreateFullPortalSession = (
  options?: UseMutationOptions<FullPortalResponseDto, Error, void>,
) => {
  return useMutation({
    mutationFn: createFullPortalSession,
    ...options,
  })
}

export const useCreatePlanUpdatePortalSession = (
  options?: UseMutationOptions<PlanUpdatePortalResponseDto, Error, PlanUpdateRequestDto>,
) => {
  return useMutation({
    mutationFn: createPlanUpdatePortalSession,
    ...options,
  })
}
